import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import { format } from 'date-fns';
import { getAuthHeader } from "../../helpers/auth";

// --- MUI ICONS ---
import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import LocationIcon from '@mui/icons-material/LocationOn';
import TimeIcon from '@mui/icons-material/AccessTime';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PhotoIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EngineeringIcon from '@mui/icons-material/Engineering';
import NotesIcon from '@mui/icons-material/Notes';
import DownloadIcon from '@mui/icons-material/Download';
import BoltIcon from '@mui/icons-material/Bolt';
import AssignmentIcon from '@mui/icons-material/Assignment';

// ─── DESIGN TOKENS ───────────────────────────────────────────────
const T = {
  bg:         'var(--nd-bg)',
  surface:    'var(--nd-surface)',
  surfaceAlt: 'var(--nd-surface-2)',
  border:     'var(--nd-w06)',
  borderMid:  'var(--nd-w10)',
  text:       'var(--nd-text)',
  textMuted:  'var(--nd-tx35)',
  textFaint:  'var(--nd-w15)',
};

// ─── SHIFT SYSTEM ────────────────────────────────────────────────
const SHIFTS = {
  morning:       { color: '#FBBF24', bg: 'rgba(251,191,36,0.18)',  label: 'Morning',       time: '4AM–11AM'  },
  afternoon:     { color: '#FB923C', bg: 'rgba(251,146,60,0.18)',  label: 'Afternoon',     time: '11AM–3PM'  },
  evening:       { color: '#34D399', bg: 'rgba(52,211,153,0.18)',  label: 'Evening',       time: '3PM–7PM'   },
  night:         { color: '#60A5FA', bg: 'rgba(96,165,250,0.18)',  label: 'Night',         time: '7PM–10PM'  },
  overnight:     { color: '#A78BFA', bg: 'rgba(167,139,250,0.18)', label: 'Overnight',     time: '10PM–4AM'  },
  rescheduled:   { color: '#94A3B8', bg: 'rgba(148,163,184,0.18)', label: 'Rescheduled',   time: ''          },
  day_extension: { color: '#F87171', bg: 'rgba(248,113,113,0.18)', label: 'Day Extension', time: ''          },
};

const STATUS_COLORS = {
  completed:    '#22D3A5',
  in_progress:  '#60A5FA',
  started:      '#60A5FA',
  before_photos:'#A78BFA',
  after_photos: '#F9A8D4',
  scheduled:    '#94A3B8',
  rescheduled:  '#F59E0B',
};

// ─── GLOBAL STYLES INJECTED ONCE ─────────────────────────────────
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');

  .novaa-cal * { box-sizing: border-box; }
  .novaa-cal ::-webkit-scrollbar { width: 4px; height: 4px; }
  .novaa-cal ::-webkit-scrollbar-track { background: transparent; }
  .novaa-cal ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }

  .novaa-cal table { border-collapse: collapse; }

  .novaa-cell {
    transition: background 0.15s ease;
    cursor: pointer;
  }
  .novaa-cell:hover { background: rgba(96,165,250,0.08) !important; }

  .novaa-badge {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 22px; height: 20px; border-radius: 5px;
    font-size: 10px; font-weight: 700; color: var(--nd-text-strong);
    transition: transform 0.12s ease, box-shadow 0.12s ease;
    cursor: pointer; padding: 0 4px;
    font-family: 'DM Mono', monospace;
  }
  .novaa-badge:hover { transform: scale(1.15); box-shadow: 0 4px 14px rgba(0,0,0,0.4); z-index: 2; position: relative; }

  .novaa-ghost {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 22px; height: 20px; border-radius: 5px;
    font-size: 10px; font-weight: 600; color: var(--nd-tx40);
    border: 1.5px dashed var(--nd-w20); 
    background: var(--nd-w03);
    font-family: 'DM Mono', monospace;
    cursor: pointer;
  }
  .novaa-ghost:hover { border-color: var(--nd-tx40); color: var(--nd-tx70); }

  .novaa-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    z-index: 10000; padding: 20px;
  }
  .novaa-modal {
    background: var(--nd-surface);
    border: 1px solid var(--nd-w08);
    border-radius: 20px;
    width: 100%; max-width: 860px;
    max-height: 88vh; overflow-y: auto;
    box-shadow: 0 32px 80px rgba(0,0,0,0.6);
  }

  .novaa-step-btn {
    padding: 14px 18px;
    border-radius: 12px;
    border: 1.5px solid var(--nd-w08);
    background: var(--nd-w03);
    color: var(--nd-text);
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600;
    cursor: pointer;
    transition: all 0.18s ease;
    text-align: left;
  }
  .novaa-step-btn:hover { background: var(--nd-w08); border-color: var(--nd-w15); }
  .novaa-step-btn.active { background: rgba(96,165,250,0.12); border-color: rgba(96,165,250,0.5); color: #60A5FA; }

  .novaa-site-card {
    padding: 10px 14px;
    border-radius: 10px;
    border: 1.5px solid var(--nd-w08);
    background: var(--nd-w03);
    cursor: pointer;
    display: flex; align-items: center; gap: 10;
    transition: all 0.15s ease;
  }
  .novaa-site-card:hover { background: var(--nd-w06); }
  .novaa-site-card.selected { border-color: rgba(96,165,250,0.5); background: rgba(96,165,250,0.08); }

  .novaa-team-card {
    padding: 16px;
    border-radius: 12px;
    border: 1.5px solid var(--nd-w08);
    background: var(--nd-w03);
    cursor: pointer;
    transition: all 0.18s ease;
    text-align: center;
  }
  .novaa-team-card:hover { background: rgba(96,165,250,0.08); border-color: rgba(96,165,250,0.4); transform: translateY(-2px); }

  .novaa-select {
    padding: 9px 12px;
    background: var(--nd-w05);
    border: 1px solid var(--nd-w10);
    border-radius: 8px;
    color: var(--nd-text);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    outline: none;
    cursor: pointer;
  }
  .novaa-select option { background: var(--nd-surface-3); color: var(--nd-text); }
  .novaa-select:focus { border-color: rgba(96,165,250,0.5); }

  .novaa-btn-primary {
    padding: 10px 22px;
    background: linear-gradient(135deg, #3B82F6, #22D3A5);
    border: none; border-radius: 10px;
    color: var(--nd-text-strong); font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 700;
    cursor: pointer; transition: opacity 0.15s ease;
  }
  .novaa-btn-primary:hover { opacity: 0.88; }
  .novaa-btn-primary:disabled { background: var(--nd-w10); color: var(--nd-tx30); cursor: not-allowed; }

  .novaa-btn-ghost {
    padding: 9px 18px;
    background: var(--nd-w05);
    border: 1px solid var(--nd-w10);
    border-radius: 10px;
    color: var(--nd-tx60);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .novaa-btn-ghost:hover { background: var(--nd-w08); color: var(--nd-text); }

  .novaa-btn-danger {
    padding: 9px 18px;
    background: rgba(248,113,113,0.12);
    border: 1px solid rgba(248,113,113,0.3);
    border-radius: 10px; color: #F87171;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer; transition: all 0.15s ease;
  }
  .novaa-btn-danger:hover { background: rgba(248,113,113,0.2); }

  .novaa-back-link {
    background: none; border: none;
    color: var(--nd-tx40);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; cursor: pointer;
    display: inline-flex; align-items: center; gap: 6px;
    padding: 0; margin-bottom: 16px;
    transition: color 0.15s;
  }
  .novaa-back-link:hover { color: var(--nd-tx70); }

  .novaa-input {
    width: 100%; padding: 10px 14px;
    background: var(--nd-w05);
    border: 1px solid var(--nd-w10);
    border-radius: 10px; color: var(--nd-text);
    font-family: 'DM Sans', sans-serif; font-size: 14px;
    outline: none; transition: border-color 0.15s;
  }
  .novaa-input:focus { border-color: rgba(96,165,250,0.5); }

  .timeline-dot {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .photo-thumb {
    width: 100%; height: 140px; object-fit: cover;
    border-radius: 10px; cursor: pointer;
    border: 1px solid var(--nd-w08);
    transition: transform 0.15s; 
  }
  .photo-thumb:hover { transform: scale(1.03); }

  .fade-in { animation: fadeIn 0.3s ease; }
  @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }

  /* Override Bootstrap Modal styles for dark theme */
  .novaa-cal .modal-content {
    background: var(--nd-surface) !important;
    border: 1px solid var(--nd-w08) !important;
    border-radius: 20px !important;
    color: var(--nd-text) !important;
  }
  .novaa-cal .modal-backdrop {
    background: rgba(0,0,0,0.7) !important;
    backdrop-filter: blur(8px) !important;
  }
`;

const UnifiedSchedulingCalendar = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [clients, setClients] = useState([]);
  const [billingMap, setBillingMap] = useState({}); // client_id -> man-hours billing status
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const config = getAuthHeader();

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [assignmentStep, setAssignmentStep] = useState('sites');
  const [clientSites, setClientSites] = useState([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);

  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    scheduleId: null, newDate: '', reason: '', originalDate: ''
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [viewMode, setViewMode] = useState('cleaning');
  const [maintenanceTemplates, setMaintenanceTemplates] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedShift, setSelectedShift] = useState('');

  // ── Obsidian grid state ──
  const [selectedDay,  setSelectedDay]  = useState(null);
  const [activeTeams,  setActiveTeams]  = useState(new Set());
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);

  // ── Bulk multi-day assign state (cleaning mode) ──
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkClientId, setBulkClientId] = useState(null);
  const [bulkStep, setBulkStep] = useState('client');
  const [bulkSiteIds, setBulkSiteIds] = useState([]);
  const [bulkShift, setBulkShift] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // ── Per-day job delete-select state ──
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());

  // ── Day-panel client search + multi-day bulk-delete modal ──
  const [clientSearch, setClientSearch] = useState('');
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteSearch, setBulkDeleteSearch] = useState('');

  // ── Petty cash (per team, per day) set at assign time ──
  const [pettyCash, setPettyCash] = useState('');
  const [bulkPettyCash, setBulkPettyCash] = useState('');

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();
  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);

  // ─── PRESERVED LOGIC ─────────────────────────────────────────
  const getShiftColor  = (shift) => SHIFTS[String(shift).toLowerCase().trim()]?.color  || '#94A3B8';
  const getShiftBg     = (shift) => SHIFTS[String(shift).toLowerCase().trim()]?.bg     || 'rgba(148,163,184,0.18)';
  const getShiftDisplayName = (shift) => {
    const s = SHIFTS[String(shift).toLowerCase().trim()];
    return s ? (s.time ? `${s.label} (${s.time})` : s.label) : shift;
  };

  useEffect(() => {
    fetchTeams();
    fetchCalendar();
    fetchBillingStatus();
    if (viewMode === 'maintenance') fetchMaintenanceTemplates();
  }, [selectedMonth, selectedYear, viewMode]);

  // All-time man-hours billing status (reconciled cycles). Failure is non-fatal.
  const fetchBillingStatus = async () => {
    try {
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/ci-performance/billing-status`);
      setBillingMap(resp.data?.data || {});
    } catch { setBillingMap({}); }
  };

  const fetchTeams = async () => {
    try {
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/teams?active_only=true`);
      setTeams(resp.data.teams || []);
    } catch (err) { setTeams([]); }
  };

  const fetchMaintenanceTemplates = async () => {
    try {
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-maintenance/templates`);
      setMaintenanceTemplates(resp.data.templates || []);
    } catch (err) { setMaintenanceTemplates([]); }
  };

  // ✅ UPDATED: Accept current day schedules to filter out already assigned sites
  const fetchClientSites = async (clientId, currentDaySchedules = []) => {
    try {
      setLoadingSites(true);
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites/${clientId}`);
      const sitesData = Array.isArray(resp.data) ? resp.data : (resp.data.sites || []);
      setClientSites(sitesData);
      
      // Filter out sites that are already assigned today
      const assignedIds = currentDaySchedules.map(s => Number(s.site_id)).filter(id => !isNaN(id));
      const availableSites = sitesData.map(s => s.id).filter(id => !assignedIds.includes(Number(id)));
      
      setSelectedSiteIds(availableSites); // Default select only available sites
    } catch (err) { setClientSites([]); setSelectedSiteIds([]); }
    finally { setLoadingSites(false); }
  };

  const fetchCalendar = async () => {
    try {
      setLoading(true); setError(null);
      if (viewMode === 'cleaning') {
        const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/calendar/${selectedMonth}/${selectedYear}`);
        if (!resp.data || !Array.isArray(resp.data.clients)) { setClients([]); setError('Invalid calendar response'); }
        else setClients(resp.data.clients || []);
      } else {
        const [clientsResp, schedulesResp] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/calendar/${selectedMonth}/${selectedYear}`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-maintenance/calendar/${selectedMonth}/${selectedYear}`).catch(() => ({ data: { schedules: {} } }))
        ]);
        const allClients = clientsResp.data.clients || [];
        const schedulesByDate = schedulesResp.data.schedules || {};
        const scheduleMap = {};
        Object.entries(schedulesByDate).forEach(([date, schedules]) => {
          schedules.forEach(schedule => { scheduleMap[`${schedule.client_id}-${date}`] = { ...schedule, scheduled_date: date }; });
        });
        setClients(allClients.map(client => ({
          client_id: client.client_id, client_name: client.client_name, total_sites: client.total_sites,
          contract_cycles: client.contract_cycles, jobs_per_cycle: client.jobs_per_cycle, completed_jobs: client.completed_jobs,
          schedules: Object.keys(scheduleMap).filter(k => k.startsWith(`${client.client_id}-`)).map(k => scheduleMap[k])
        })));
      }
    } catch (err) { setError('Failed to load calendar data'); setClients([]); }
    finally { setLoading(false); }
  };

  const formatDateForAPI = (year, month, day) =>
    `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;

  const normalizeDateString = (d) => {
    if (!d) return '';
    const s = String(d);
    if (s.includes('T')) {
      const o = new Date(s);
      return `${o.getUTCFullYear()}-${String(o.getUTCMonth()+1).padStart(2,'0')}-${String(o.getUTCDate()).padStart(2,'0')}`;
    }
    return s;
  };

  const getSchedulesForDay = (client, day) => {
    if (!client || !Array.isArray(client.schedules)) return [];
    const target = formatDateForAPI(selectedYear, selectedMonth, day);
    return client.schedules.filter(s => s?.scheduled_date && normalizeDateString(s.scheduled_date) === target);
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return '✓';
    if (['in_progress','started','before_photos','after_photos'].includes(status)) return '▶';
    if (status === 'rescheduled') return '↻';
    return '';
  };

  // ✅ UPDATED: Pass existing schedules to fetchClientSites for filtering
  const handleCellClick = async (client, day) => {
    const existingSchedules = getSchedulesForDay(client, day);
    const dateStr = formatDateForAPI(selectedYear, selectedMonth, day);
    setClientSites([]); setSelectedSiteIds([]); setSelectedShift('');
    
    if (viewMode === 'cleaning') {
      setSelectedCell({ clientId: client.client_id, clientName: client.client_name, day, date: dateStr, existingSchedules, serviceNumber: existingSchedules.length + 1, monthlyServices: client.contractual_services_count ?? client.monthly_services ?? null, totalSites: client.total_sites ?? 0 });
      setAssignmentStep('sites');
      fetchClientSites(client.client_id, existingSchedules); 
    } else {
      setSelectedCell({ clientId: client.client_id, clientName: client.client_name, day, date: dateStr, existingSchedules, serviceNumber: 1 });
      setSelectedTasks(maintenanceTemplates.map(t => t.id));
      setAssignmentStep('teams');
    }
    setShowAssignModal(true);
  };

  const handleViewJobDetails = async (job, client) => {
    try {
      setDetailsLoading(true); setDetailsOpen(true);
      setSelectedJob({ ...job, client_name: client.client_name, job_type: viewMode === 'cleaning' ? 'commercial' : 'maintenance', site_name: job.site_name || client.client_name });
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/fo-schedules/${job.schedule_id}/detail?client_id=${client.client_id}`, config);
      setSelectedJob(prev => ({ ...prev, ...response.data.job }));
    } catch (error) { console.error("Error fetching job details:", error); }
    finally { setDetailsLoading(false); }
  };

  const handleAssignTeam = async (teamId) => {
    if (!selectedCell) return;
    if (!selectedShift) { alert('❌ Please select a shift before assigning a team'); return; }
    try {
      if (viewMode === 'cleaning') {
        const payload = { client_id: Number(selectedCell.clientId), team_ids: [Number(teamId)], scheduled_date: selectedCell.date, service_number: selectedCell.serviceNumber, status: 'scheduled', site_ids: selectedSiteIds, shift: selectedShift };
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/schedule/bulk-by-client`, payload);
        alert(`✓ Team assigned — ${getShiftDisplayName(selectedShift)} shift`);
      } else {
        const payload = { client_id: Number(selectedCell.clientId), team_id: Number(teamId), scheduled_date: selectedCell.date, service_number: selectedCell.serviceNumber, exclude_task_ids: maintenanceTemplates.filter(t => !selectedTasks.includes(t.id)).map(t => t.id), shift: selectedShift };
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/commercial-maintenance/schedule-tasks`, payload);
        alert(`✓ Maintenance tasks scheduled — ${getShiftDisplayName(selectedShift)} shift`);
      }
      // Petty cash for this team's day (per team, per day) — non-blocking
      if (pettyCash && Number(pettyCash) > 0) {
        try {
          await axios.post(`${process.env.REACT_APP_API_BASE_URL}/ci-petty-cash/request`, {
            team_id: Number(teamId), for_date: selectedCell.date, amount: Number(pettyCash), requested_by: 'ci_admin',
          });
        } catch (e) { /* petty cash request is non-blocking */ }
      }
      setShowAssignModal(false); setSelectedTasks([]); setSelectedShift(''); setPettyCash(''); fetchCalendar();
    } catch (err) { alert(`❌ Error: ${err?.response?.data?.message || 'Failed'}`); }
  };

  const handleUpdateSchedule = async (scheduleId, updates) => {
    try {
      const sanitized = { ...updates };
      if (sanitized.team_id !== undefined) sanitized.team_id = Number(sanitized.team_id);
      const url = viewMode === 'cleaning' ? `${process.env.REACT_APP_API_BASE_URL}/schedule/${scheduleId}` : `${process.env.REACT_APP_API_BASE_URL}/commercial-maintenance/task/${scheduleId}`;
      await axios.put(url, sanitized);
      alert('Schedule updated!'); fetchCalendar();
    } catch (err) { alert('Failed to update schedule'); }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/schedule/${scheduleId}`);
      alert('Schedule deleted!'); setShowAssignModal(false); fetchCalendar();
    } catch (err) { alert('Failed to delete schedule'); }
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleData.newDate || !rescheduleData.reason.trim()) { alert('❌ Please select a new date and provide a reason.'); return; }
    try {
      const url = viewMode === 'cleaning' ? `${process.env.REACT_APP_API_BASE_URL}/schedule/${rescheduleData.scheduleId}` : `${process.env.REACT_APP_API_BASE_URL}/commercial-maintenance/task/${rescheduleData.scheduleId}`;
      await axios.put(url, { scheduled_date: rescheduleData.newDate, reschedule_reason: rescheduleData.reason, status: 'rescheduled', shift: 'rescheduled' });
      alert('✅ Schedule moved successfully!');
      setShowRescheduleModal(false); setShowAssignModal(false);
      setRescheduleData({ scheduleId: null, newDate: '', reason: '', originalDate: '' });
      fetchCalendar();
    } catch (err) { alert('❌ Failed to reschedule. ' + (err?.response?.data?.message || '')); }
  };

  const handleDownloadReport = () => alert("Downloading report...");

  const toggleTaskSelection  = (taskId) => setSelectedTasks(prev => prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]);
  const toggleSiteSelection  = (siteId) => setSelectedSiteIds(prev => prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]);
  
  // ✅ UPDATED: Select only available sites
  const handleSelectAllSites   = () => {
     const assignedIds = (selectedCell?.existingSchedules || []).map(s => Number(s.site_id)).filter(id => !isNaN(id));
     const availableSites = clientSites.map(s => s.id).filter(id => !assignedIds.includes(Number(id)));
     setSelectedSiteIds(availableSites);
  };
  const handleDeselectAllSites = () => setSelectedSiteIds([]);

  const toggleTeam = (id) => {
    const next = new Set(activeTeams);
    next.has(id) ? next.delete(id) : next.add(id);
    setActiveTeams(next);
  };

  // Auto-activate all teams when they load
  React.useEffect(() => {
    if (teams.length > 0 && activeTeams.size === 0) {
      setActiveTeams(new Set(teams.map(t => t.id)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams]);

  // ── BULK MULTI-DAY ASSIGN ──
  // Clear bulk selection whenever the period or view mode changes.
  useEffect(() => {
    setSelectedDays(new Set());
    setBulkMode(false);
    setShowBulkModal(false);
    setShowBulkDeleteModal(false);
  }, [selectedMonth, selectedYear, viewMode]);

  const toggleBulkMode = () => {
    setBulkMode(prev => {
      const next = !prev;
      if (next) setSelectedDay(null);                       // close single-day panel
      else { setSelectedDays(new Set()); setShowBulkModal(false); setShowBulkDeleteModal(false); }
      return next;
    });
  };

  const toggleDaySelection = (day) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      next.has(day) ? next.delete(day) : next.add(day);
      return next;
    });
  };

  const fetchBulkClientSites = async (clientId) => {
    try {
      setLoadingSites(true);
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-sites/${clientId}`);
      const sitesData = Array.isArray(resp.data) ? resp.data : (resp.data.sites || []);
      setClientSites(sitesData);
      setBulkSiteIds(sitesData.map(s => s.id)); // default: all sites selected
    } catch (err) { setClientSites([]); setBulkSiteIds([]); }
    finally { setLoadingSites(false); }
  };

  const openBulkModal = () => {
    if (selectedDays.size === 0) return;
    setBulkClientId(null); setBulkSiteIds([]); setBulkShift(''); setClientSites([]);
    setBulkStep('client');
    setShowBulkModal(true);
  };

  const handleBulkClientPick = (clientId) => {
    setBulkClientId(clientId);
    setBulkSiteIds([]);
    fetchBulkClientSites(clientId);
    setBulkStep('sites');
  };

  const toggleBulkSite = (siteId) =>
    setBulkSiteIds(prev => prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]);

  const handleBulkAssign = async (teamId) => {
    if (!bulkClientId)            { alert('❌ Please select a client'); return; }
    if (bulkSiteIds.length === 0) { alert('❌ Please select at least one site'); return; }
    if (!bulkShift)               { alert('❌ Please select a shift'); return; }
    if (selectedDays.size === 0)  { alert('❌ Please select at least one day'); return; }

    const scheduled_dates = [...selectedDays]
      .sort((a, b) => a - b)
      .map(d => formatDateForAPI(selectedYear, selectedMonth, d));

    try {
      setBulkSubmitting(true);
      const payload = {
        client_id: Number(bulkClientId),
        team_ids: [Number(teamId)],
        scheduled_dates,
        site_ids: bulkSiteIds,
        shift: bulkShift,
        status: 'scheduled',
      };
      const resp = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/schedule/bulk-multi-day`, payload);
      const n = resp.data?.created_count ?? scheduled_dates.length;
      alert(`✓ Bulk assigned — ${n} day(s), ${getShiftDisplayName(bulkShift)} shift`);
      // Petty cash per selected day (per team, per day) — non-blocking
      if (bulkPettyCash && Number(bulkPettyCash) > 0) {
        await Promise.allSettled(scheduled_dates.map(d =>
          axios.post(`${process.env.REACT_APP_API_BASE_URL}/ci-petty-cash/request`, {
            team_id: Number(teamId), for_date: d, amount: Number(bulkPettyCash), requested_by: 'ci_admin',
          })
        ));
      }
      setShowBulkModal(false);
      setBulkMode(false);
      setSelectedDays(new Set());
      setBulkPettyCash('');
      fetchCalendar();
    } catch (err) {
      alert(`❌ Error: ${err?.response?.data?.message || 'Failed to bulk assign'}`);
    } finally {
      setBulkSubmitting(false);
    }
  };

  // Reset job delete-selection whenever the focused day / period / view changes.
  useEffect(() => {
    setDeleteMode(false);
    setSelectedJobIds(new Set());
    setClientSearch('');
  }, [selectedDay, selectedMonth, selectedYear, viewMode]);

  const toggleJobSelection = (scheduleId) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev);
      next.has(scheduleId) ? next.delete(scheduleId) : next.add(scheduleId);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedJobIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedJobIds.size} selected job(s)? This cannot be undone.`)) return;
    try {
      setBulkSubmitting(true);
      const ids = [...selectedJobIds];
      const resp = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/schedule/bulk-delete`, { schedule_ids: ids });
      const n = resp.data?.deleted_count ?? ids.length;
      alert(`🗑 Deleted ${n} job(s)`);
      setDeleteMode(false);
      setSelectedJobIds(new Set());
      fetchCalendar();
    } catch (err) {
      alert(`❌ Error: ${err?.response?.data?.message || 'Failed to delete jobs'}`);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const openBulkDeleteModal = () => {
    if (selectedDays.size === 0) return;
    setBulkDeleteSearch('');
    setShowBulkDeleteModal(true);
  };

  // Delete one client's jobs across all selected days (route: bulk-delete-by-dates).
  const handleBulkDeleteByDates = async (clientObj) => {
    const scheduled_dates = [...selectedDays]
      .sort((a, b) => a - b)
      .map(d => formatDateForAPI(selectedYear, selectedMonth, d));

    let jobCount = 0;
    selectedDays.forEach(day => { jobCount += getSchedulesForDay(clientObj, day).length; });

    if (!window.confirm(`Delete ${jobCount} job(s) for ${clientObj.client_name} across ${scheduled_dates.length} selected day(s)? This cannot be undone.`)) return;

    try {
      setBulkSubmitting(true);
      const resp = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/schedule/bulk-delete-by-dates`, {
        client_id: Number(clientObj.client_id),
        scheduled_dates,
      });
      const n = resp.data?.deleted_count ?? jobCount;
      alert(`🗑 Deleted ${n} job(s)`);
      setShowBulkDeleteModal(false);
      setBulkMode(false);
      setSelectedDays(new Set());
      fetchCalendar();
    } catch (err) {
      alert(`❌ Error: ${err?.response?.data?.message || 'Failed to delete jobs'}`);
    } finally {
      setBulkSubmitting(false);
    }
  };

  const TEAM_PALETTE = ['#FBBF24','#10B981','#6366F1','#EC4899','#60A5FA','#FB923C','#A78BFA','#F87171'];
  const getTeamColor = (team, idx) => team.color || TEAM_PALETTE[idx % TEAM_PALETTE.length];

  const getAllSchedulesForDay = (day) => {
    const results = [];
    clients.forEach(client => {
      const schedules = getSchedulesForDay(client, day);
      schedules.forEach(s => {
        if (activeTeams.size === 0 || activeTeams.has(s.team_id)) {
          results.push({ ...s, client_name: client.client_name, client_id: client.client_id, _client: client });
        }
      });
    });
    return results;
  };

  const firstDayOfMonth = new Date(selectedYear, selectedMonth - 1, 1).getDay();
  const daysInMonthCalc = getDaysInMonth(selectedMonth, selectedYear);
  const DAYS_OF_WEEK_LABELS = ['SUN','MON','TUE','WED','THU','FRI','SAT'];


  // ─── MONTH-LEVEL STATS ───────────────────────────────────────
  // Scope reflects the active-team filter AND, if a day is selected, that day.
  const teamOK = (s) => activeTeams.size === 0 || activeTeams.has(s.team_id);
  const scopeTargetDate = selectedDay ? formatDateForAPI(selectedYear, selectedMonth, selectedDay) : null;
  const inScope = (s) => {
    if (!teamOK(s)) return false;
    if (scopeTargetDate) return s?.scheduled_date && normalizeDateString(s.scheduled_date) === scopeTargetDate;
    return true;
  };

  let scopedClientCount = 0;
  const scopedSchedules = [];
  clients.forEach(c => {
    const forClient = (c.schedules || []).filter(inScope);
    if (forClient.length > 0) scopedClientCount += 1;
    for (const s of forClient) scopedSchedules.push(s);
  });

  const teamsNarrowed = teams.length > 0 && activeTeams.size > 0 && activeTeams.size < teams.length;
  const clientsInScope = (selectedDay || teamsNarrowed) ? scopedClientCount : clients.length;

  const stats = {
    total:       scopedSchedules.length,
    completed:   scopedSchedules.filter(s => s.status === 'completed').length,
    active:      scopedSchedules.filter(s => ['in_progress','started','before_photos','after_photos'].includes(s.status)).length,
    rescheduled: scopedSchedules.filter(s => s.status === 'rescheduled').length,
    scheduled:   scopedSchedules.filter(s => s.status === 'scheduled').length,
  };

  // Billing-ready = man-hours cycles complete (reconciled from job counts). Source of
  // truth is the backend /ci-performance/billing-status (assumed 8h/day per present
  // member vs the client's mandate; ready when cycles ≥ contract_cycles, or ≥1 unset).
  const billingReadyClients = clients.filter(c => billingMap[c.client_id] && billingMap[c.client_id].billing_ready);

  const todayDay = new Date().getDate();
  const todayMonth = new Date().getMonth() + 1;
  const todayYear = new Date().getFullYear();
  const isCurrentMonthYear = selectedMonth === todayMonth && selectedYear === todayYear;

  // ─────────────────────────────────────────────────────────────
  return (
    <div
      className="novaa-cal"
      style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 18px' }}
    >
      <style>{GLOBAL_STYLES}</style>

      {/* ══════════════════════════════════════════════════════════
          HEADER
      ══════════════════════════════════════════════════════════ */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 14, flexShrink: 0, overflow: 'hidden' }}>
        {/* Breadcrumb + title */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · C&I MANAGEMENT</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Team Scheduling Calendar</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 5 }}>Commercial & Industrial Field Operations</div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)', marginBottom: 0 }} />
        {/* Controls strip */}
        <div style={{ padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 0, borderBottom: `1px solid ${T.border}` }}>

          {/* View Toggle */}
          <div style={{ display: 'flex', gap: 4, background: 'var(--nd-w04)', padding: 3, borderRadius: 10, border: `1px solid ${T.border}`, marginRight: 22, flexShrink: 0 }}>
            {[['cleaning','🧹','Cleaning'],['maintenance','🔧','Maintenance']].map(([mode, icon, label]) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{ padding: '8px 16px', background: viewMode === mode ? 'linear-gradient(135deg,#3B82F6,#22D3A5)' : 'transparent', color: viewMode === mode ? 'var(--nd-text-strong)' : T.textMuted, border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12, fontFamily: "'DM Sans', sans-serif", display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s', whiteSpace: 'nowrap' }}
              >
                {icon} {label}
              </button>
            ))}
          </div>

          {/* Month / Year */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 22, flexShrink: 0 }}>
            <button onClick={() => { if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(y => y-1); } else setSelectedMonth(m => m-1); }}
              style={{ background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, width: 32, height: 32, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
            <div style={{ textAlign: 'center', minWidth: 110 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em' }}>{monthNames[selectedMonth-1]} {selectedYear}</div>
              {isCurrentMonthYear && <div style={{ fontSize: 9, color: '#60A5FA', letterSpacing: '0.1em', marginTop: 1 }}>CURRENT MONTH</div>}
            </div>
            <button onClick={() => { if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(y => y+1); } else setSelectedMonth(m => m+1); }}
              style={{ background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, width: 32, height: 32, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
          </div>

          <div style={{ display: 'flex', gap: 8, marginRight: 22 }}>
            <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))} className="novaa-select" style={{ width: 80 }}>
              {monthNames.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="novaa-select" style={{ width: 80 }}>
              {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <button onClick={fetchCalendar} className="novaa-btn-ghost" style={{ marginRight: 'auto', flexShrink: 0 }}>↻ Refresh</button>
          {billingReadyClients.length > 0 && (
            <div
              title={`Ready for billing: ${billingReadyClients.map(c => c.client_name).join(', ')}`}
              style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, padding: '6px 12px', borderRadius: 8, background: 'rgba(34,211,165,0.14)', border: '1px solid rgba(34,211,165,0.45)', cursor: 'default' }}
            >
              <span style={{ fontSize: 14 }}>🔔</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#22D3A5', fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{billingReadyClients.length}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#22D3A5', letterSpacing: '0.06em' }}>READY FOR BILLING</span>
            </div>
          )}
        </div>

        {/* Scope indicator — what the stats below reflect */}
        <div style={{ padding: '7px 22px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 8, fontSize: 10 }}>
          <span style={{ fontWeight: 800, letterSpacing: '0.14em', color: T.textMuted }}>SHOWING</span>
          <span style={{ color: '#60A5FA', fontWeight: 700 }}>
            {selectedDay ? `${monthNames[selectedMonth - 1]} ${selectedDay}, ${selectedYear}` : `${monthNames[selectedMonth - 1]} ${selectedYear}`}
          </span>
          <span style={{ color: T.textFaint }}>·</span>
          <span style={{ color: '#22D3A5', fontWeight: 700 }}>
            {(activeTeams.size === 0 || activeTeams.size === teams.length) ? 'All teams' : `${activeTeams.size}/${teams.length} teams`}
          </span>
          {selectedDay && (
            <button
              onClick={() => setSelectedDay(null)}
              style={{ marginLeft: 6, background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 6, color: T.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', cursor: 'pointer' }}
            >
              ✕ CLEAR DAY · SHOW MONTH
            </button>
          )}
        </div>

        {/* Stats bar — THE BOSS PANEL */}
        <div style={{ display: 'flex', alignItems: 'stretch', borderRadius: '0 0 16px 16px', overflow: 'hidden' }}>
          {[
            { label: 'CLIENTS',     value: clientsInScope,    color: 'var(--nd-tx60)', accent: 'var(--nd-w12)' },
            { label: 'TOTAL JOBS',  value: stats.total,      color: 'var(--nd-tx60)', accent: 'var(--nd-w08)' },
            { label: 'SCHEDULED',   value: stats.scheduled,  color: '#94A3B8',               accent: 'rgba(148,163,184,0.08)' },
            { label: 'ACTIVE NOW',  value: stats.active,     color: '#60A5FA',               accent: 'rgba(96,165,250,0.08)'  },
            { label: 'COMPLETED',   value: stats.completed,  color: '#22D3A5',               accent: 'rgba(34,211,165,0.08)'  },
            { label: 'RESCHEDULED', value: stats.rescheduled,color: '#F59E0B',               accent: 'rgba(245,158,11,0.08)'  },
          ].map(({ label, value, color, accent }) => (
            <div key={label} style={{ flex: 1, padding: '12px 0', textAlign: 'center', background: accent, borderRight: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 24, fontWeight: 800, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{loading ? '—' : value}</div>
              <div style={{ fontSize: 8, color: T.textMuted, letterSpacing: '0.14em', marginTop: 4, fontWeight: 600 }}>{label}</div>
            </div>
          ))}
          <div style={{ flex: 1, padding: '12px 0', textAlign: 'center', background: 'var(--nd-w02)' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: stats.total > 0 ? '#22D3A5' : T.textMuted, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>
              {stats.total > 0 ? `${Math.round((stats.completed / stats.total) * 100)}%` : '—'}
            </div>
            <div style={{ fontSize: 8, color: T.textMuted, letterSpacing: '0.14em', marginTop: 4, fontWeight: 600 }}>COMPLETION</div>
          </div>
        </div>
      </div>

      {/* ── FILTER ROW ── */}
      <div style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12,
        padding: '9px 16px', marginBottom: 14,
        display: 'flex', alignItems: 'center', gap: 16,
      }}>

        {/* Teams dropdown */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setTeamDropdownOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
              background: teamDropdownOpen ? 'rgba(96,165,250,0.1)' : 'var(--nd-w04)',
              border: `1px solid ${teamDropdownOpen ? 'rgba(96,165,250,0.4)' : 'var(--nd-w08)'}`,
              color: 'var(--nd-text)', fontFamily: "'DM Sans', sans-serif",
              fontSize: 12, fontWeight: 700, transition: 'all 0.15s',
            }}
          >
            <i className="feather icon-users" style={{ fontSize: 12, color: '#60A5FA' }} />
            Teams
            <span style={{
              fontSize: 10, fontFamily: "'DM Mono', monospace", fontWeight: 700,
              background: 'rgba(96,165,250,0.15)', color: '#60A5FA',
              borderRadius: 5, padding: '1px 6px', border: '1px solid rgba(96,165,250,0.25)',
            }}>
              {activeTeams.size} / {teams.length}
            </span>
            <span style={{ fontSize: 9, color: 'var(--nd-tx25)', marginLeft: 2, display: 'inline-block', transform: teamDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
          </button>

          {/* Dropdown panel */}
          {teamDropdownOpen && (
            <div
              style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
                background: 'var(--nd-surface-2)', border: '1px solid var(--nd-w08)',
                borderRadius: 12, padding: '8px 6px',
                boxShadow: '0 16px 40px rgba(0,0,0,0.5)',
                minWidth: 220, maxHeight: 320, overflowY: 'auto',
                animation: 'fadeIn 0.15s ease',
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* All / None */}
              <div style={{ display: 'flex', gap: 6, padding: '4px 8px 8px', borderBottom: '1px solid var(--nd-w06)', marginBottom: 6 }}>
                <button
                  onClick={() => setActiveTeams(new Set(teams.map(t => t.id)))}
                  style={{ flex: 1, padding: '5px 0', borderRadius: 7, border: '1px solid var(--nd-w08)', background: 'var(--nd-w04)', color: 'var(--nd-tx45)', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.06em' }}
                >ALL</button>
                <button
                  onClick={() => setActiveTeams(new Set())}
                  style={{ flex: 1, padding: '5px 0', borderRadius: 7, border: '1px solid var(--nd-w08)', background: 'var(--nd-w04)', color: 'var(--nd-tx45)', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.06em' }}
                >NONE</button>
              </div>

              {/* Team list */}
              {teams.map((team, idx) => {
                const color  = getTeamColor(team, idx);
                const active = activeTeams.has(team.id);
                return (
                  <div
                    key={team.id}
                    onClick={() => toggleTeam(team.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9,
                      padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
                      background: active ? color + '10' : 'transparent',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--nd-w04)'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Checkbox */}
                    <div style={{
                      width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                      border: `1.5px solid ${active ? color : 'var(--nd-w20)'}`,
                      background: active ? color : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.12s',
                    }}>
                      {active && <span style={{ fontSize: 9, color: 'var(--nd-bg)', fontWeight: 900, lineHeight: 1 }}>✓</span>}
                    </div>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0, boxShadow: active ? `0 0 5px ${color}` : 'none', opacity: active ? 1 : 0.4 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: active ? 'var(--nd-text)' : 'var(--nd-tx35)', fontFamily: "'DM Sans', sans-serif" }}>
                      {team.team_lead_name || `Team ${team.id}`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ width: 1, height: 20, background: T.border, flexShrink: 0 }} />

        {/* Shift legend — compact single row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'nowrap', overflow: 'hidden' }}>
          {Object.entries(SHIFTS).slice(0, 5).map(([key, s]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color, boxShadow: `0 0 5px ${s.color}50`, flexShrink: 0 }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, whiteSpace: 'nowrap' }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Rescheduled indicator */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <div style={{ width: 20, height: 10, borderRadius: 3, border: '1.5px dashed var(--nd-w20)', background: 'var(--nd-w03)' }} />
          <span style={{ fontSize: 10, fontWeight: 600, color: T.textMuted }}>Rescheduled</span>
        </div>

        {/* Bulk-assign toggle (cleaning mode only) */}
        {viewMode === 'cleaning' && (
          <button
            onClick={toggleBulkMode}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0,
              padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
              background: bulkMode ? 'rgba(34,211,165,0.14)' : 'var(--nd-w04)',
              border: `1px solid ${bulkMode ? 'rgba(34,211,165,0.5)' : 'var(--nd-w08)'}`,
              color: bulkMode ? '#22D3A5' : 'var(--nd-text)',
              fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, transition: 'all 0.15s',
            }}
          >
            <span style={{ fontSize: 13 }}>⊞</span>
            {bulkMode ? 'Bulk: ON' : 'Bulk Assign'}
          </button>
        )}

        {/* Click-outside to close */}
        {teamDropdownOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setTeamDropdownOpen(false)} />
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════
          MAIN AREA: Monthly Grid + Day Detail Panel
      ══════════════════════════════════════════════════════════ */}
      {(() => {
        const gridCells = [];
        for (let i = 0; i < firstDayOfMonth; i++) gridCells.push(null);
        for (let d = 1; d <= daysInMonthCalc; d++) gridCells.push(d);

        const selectedDayJobs = selectedDay ? getAllSchedulesForDay(selectedDay) : [];
        const jobsByClient = {};
        selectedDayJobs.forEach(j => {
          if (!jobsByClient[j.client_id]) jobsByClient[j.client_id] = { client_name: j.client_name, _client: j._client, jobs: [] };
          jobsByClient[j.client_id].jobs.push(j);
        });

        return (
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>

            {/* ── MONTHLY GRID ── */}
            <div style={{ flex: 1, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', minWidth: 0 }}>
              {loading && (
                <div style={{ padding: '60px 0', textAlign: 'center', color: T.textMuted, fontSize: 13 }}>
                  <div style={{ fontSize: 22, marginBottom: 8 }}>⏳</div>Loading calendar…
                </div>
              )}
              {error && !loading && (
                <div style={{ padding: '32px', textAlign: 'center', color: '#F87171', fontSize: 13 }}>⚠ {error}</div>
              )}
              {!loading && (
                <div style={{ padding: '16px 16px 20px' }}>
                  {/* Day headers */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 6 }}>
                    {DAYS_OF_WEEK_LABELS.map(d => (
                      <div key={d} style={{
                        textAlign: 'center', fontSize: 9, fontWeight: 800, letterSpacing: '0.15em',
                        color: (d === 'SUN' || d === 'SAT') ? 'var(--nd-w15)' : 'var(--nd-tx22)',
                        padding: '4px 0 8px', borderBottom: `1px solid ${T.border}`,
                      }}>{d}</div>
                    ))}
                  </div>
                  {/* Grid cells */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '110px', gap: 6 }}>
                    {gridCells.map((day, i) => {
                      if (!day) return <div key={`e-${i}`} />;
                      const daySchedules = getAllSchedulesForDay(day);
                      const isSelected = selectedDay === day;
                      const isToday = isCurrentMonthYear && day === todayDay;
                      const isBulkPicked = bulkMode && selectedDays.has(day);
                      const dow = (firstDayOfMonth + day - 1) % 7;
                      const isWeekend = dow === 0 || dow === 6;

                      const strips = [];
                      daySchedules.forEach(s => {
                        let sk = s.shift || s.time_slot || '';
                        if (s.status === 'rescheduled') sk = 'rescheduled';
                        if (strips.length < 4) strips.push(getShiftColor(sk));
                      });
                      const overflow = daySchedules.length - 4;

                      return (
                        <div
                          key={day}
                          onClick={() => bulkMode ? toggleDaySelection(day) : setSelectedDay(isSelected ? null : day)}
                          style={{
                            border: `1px solid ${isBulkPicked ? 'rgba(34,211,165,0.6)' : isSelected ? 'rgba(59,130,246,0.5)' : isToday ? 'rgba(96,165,250,0.3)' : 'var(--nd-w05)'}`,
                            borderRadius: 10, padding: '10px 11px 8px',
                            cursor: 'pointer', position: 'relative',
                            background: isBulkPicked
                              ? 'rgba(34,211,165,0.12)'
                              : isSelected
                              ? 'rgba(59,130,246,0.12)'
                              : isToday ? 'rgba(96,165,250,0.06)'
                              : isWeekend ? 'var(--nd-w02)'
                              : 'var(--nd-w02)',
                            transition: 'all 0.15s',
                            boxShadow: isBulkPicked
                              ? '0 0 0 1px rgba(34,211,165,0.4), inset 0 0 20px rgba(34,211,165,0.05)'
                              : isSelected ? '0 0 0 1px rgba(59,130,246,0.3), inset 0 0 20px rgba(59,130,246,0.04)' : 'none',
                          }}
                          onMouseEnter={e => { if (!isSelected && !isBulkPicked) { e.currentTarget.style.background = 'rgba(96,165,250,0.07)'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.28)'; } }}
                          onMouseLeave={e => { e.currentTarget.style.background = isBulkPicked ? 'rgba(34,211,165,0.12)' : isSelected ? 'rgba(59,130,246,0.12)' : isToday ? 'rgba(96,165,250,0.06)' : isWeekend ? 'var(--nd-w02)' : 'var(--nd-w02)'; e.currentTarget.style.borderColor = isBulkPicked ? 'rgba(34,211,165,0.6)' : isSelected ? 'rgba(59,130,246,0.5)' : isToday ? 'rgba(96,165,250,0.3)' : 'var(--nd-w05)'; }}
                        >
                          {/* Selected left bar */}
                          {isSelected && !isBulkPicked && <div style={{ position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 2, borderRadius: '0 2px 2px 0', background: 'linear-gradient(180deg,#3B82F6,#22D3A5)', boxShadow: '0 0 8px rgba(59,130,246,0.6)' }} />}

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7 }}>
                            <div style={{
                              fontSize: 14, fontWeight: isToday ? 800 : isSelected ? 700 : 400,
                              color: isToday ? '#60A5FA' : isSelected ? 'var(--nd-text-strong)' : isWeekend ? 'var(--nd-w20)' : 'var(--nd-tx45)',
                              fontFamily: "'DM Mono', monospace", lineHeight: 1,
                            }}>
                              {day}
                              {isToday && <span style={{ display: 'inline-block', width: 4, height: 4, borderRadius: '50%', background: '#60A5FA', marginLeft: 4, verticalAlign: 'middle', boxShadow: '0 0 5px #60A5FA' }} />}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              {daySchedules.length > 0 && (
                                <span style={{
                                  fontSize: 9, fontFamily: "'DM Mono', monospace", fontWeight: 700,
                                  color: isBulkPicked ? '#22D3A5' : isSelected ? '#60A5FA' : 'var(--nd-w20)',
                                  background: isBulkPicked ? 'rgba(34,211,165,0.15)' : isSelected ? 'rgba(96,165,250,0.15)' : 'var(--nd-w04)',
                                  borderRadius: 5, padding: '2px 5px', lineHeight: 1.5,
                                }}>{daySchedules.length}</span>
                              )}
                              {isBulkPicked && (
                                <span style={{ width: 16, height: 16, borderRadius: 5, background: '#22D3A5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, color: 'var(--nd-bg)', boxShadow: '0 0 8px rgba(34,211,165,0.5)' }}>✓</span>
                              )}
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {strips.map((color, si) => (
                              <div key={si} style={{ height: 4, borderRadius: 2, background: color, boxShadow: `0 0 4px ${color}50`, opacity: 0.85 }} />
                            ))}
                            {overflow > 0 && (
                              <div style={{ fontSize: 8, color: 'var(--nd-tx25)', fontFamily: "'DM Mono', monospace", marginTop: 1 }}>+{overflow}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {!loading && clients.length === 0 && !error && (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: T.textMuted }}>
                      <div style={{ fontSize: 24, marginBottom: 8 }}>📅</div>
                      <div style={{ fontSize: 12 }}>No clients found for this period</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── DAY DETAIL PANEL ── */}
            <div style={{
              width: 320, flexShrink: 0, background: T.surface, border: `1px solid ${T.border}`,
              borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignSelf: 'stretch',
            }}>
              {selectedDay ? (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, animation: 'fadeIn 0.25s ease' }}>
                  {/* Panel header */}
                  <div style={{ padding: '18px 18px 14px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
                    <div style={{ fontSize: 8, fontWeight: 800, color: T.textMuted, letterSpacing: '0.2em', marginBottom: 4 }}>SCHEDULE FOR</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>
                      {monthNames[selectedMonth - 1]} {selectedDay}
                    </div>
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>
                      {selectedDayJobs.length} job{selectedDayJobs.length !== 1 ? 's' : ''} scheduled
                    </div>
                    {selectedDayJobs.length > 0 && (
                      <button
                        onClick={() => { setDeleteMode(m => !m); setSelectedJobIds(new Set()); }}
                        style={{
                          marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '5px 12px', borderRadius: 7, cursor: 'pointer',
                          background: deleteMode ? 'rgba(248,113,113,0.14)' : 'var(--nd-w04)',
                          border: `1px solid ${deleteMode ? 'rgba(248,113,113,0.5)' : 'var(--nd-w10)'}`,
                          color: deleteMode ? '#F87171' : T.textMuted,
                          fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                        }}
                      >
                        {deleteMode ? '✕ Cancel select' : '☑ Select to delete'}
                      </button>
                    )}
                    <div style={{ height: 1, marginTop: 12, background: 'linear-gradient(90deg, rgba(59,130,246,0.4), rgba(34,211,165,0.2), transparent)' }} />
                  </div>

                  {/* Delete action bar (job select mode) */}
                  {deleteMode && (
                    <div style={{ padding: '11px 14px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(248,113,113,0.05)', flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 700 }}>{selectedJobIds.size} selected</span>
                      <button
                        onClick={() => {
                          const all = selectedDayJobs.map(j => j.schedule_id);
                          setSelectedJobIds(prev => prev.size === all.length ? new Set() : new Set(all));
                        }}
                        className="novaa-btn-ghost"
                        style={{ fontSize: 11, padding: '6px 12px', marginLeft: 'auto' }}
                      >
                        {selectedJobIds.size === selectedDayJobs.length ? 'None' : 'All'}
                      </button>
                      <button
                        disabled={selectedJobIds.size === 0 || bulkSubmitting}
                        onClick={handleBulkDelete}
                        className="novaa-btn-danger"
                        style={{ fontSize: 11, opacity: (selectedJobIds.size === 0 || bulkSubmitting) ? 0.5 : 1 }}
                      >
                        🗑 Delete{selectedJobIds.size > 0 ? ` ${selectedJobIds.size}` : ''}
                      </button>
                    </div>
                  )}

                  {/* Scrollable body */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
                    {selectedDayJobs.length === 0 ? (
                      <div style={{ paddingTop: 48, textAlign: 'center', marginBottom: 20 }}>
                        <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.3 }}>◈</div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nd-w15)', letterSpacing: '0.2em' }}>NO JOBS SCHEDULED</div>
                      </div>
                    ) : (
                      Object.values(jobsByClient).map(({ client_name, _client, jobs }) => (
                        <div key={_client.client_id} style={{ marginBottom: 18 }}>
                          {/* Client header */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                              <div style={{ width: 22, height: 22, borderRadius: 7, background: 'linear-gradient(135deg,#3B82F6,#22D3A5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900, color: 'var(--nd-bg)' }}>
                                {client_name?.[0]?.toUpperCase() || 'C'}
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 800, color: T.text }}>{client_name}</span>
                            </div>
                            <span style={{ fontSize: 8, fontFamily: "'DM Mono', monospace", fontWeight: 600, color: '#60A5FA', background: 'rgba(96,165,250,0.1)', borderRadius: 5, padding: '2px 7px', border: '1px solid rgba(96,165,250,0.2)' }}>
                              {jobs.length}
                            </span>
                          </div>

                          {/* Job cards */}
                          {jobs.map(j => {
                            let sk = j.shift || j.time_slot || '';
                            if (j.status === 'rescheduled') sk = 'rescheduled';
                            const shiftColor  = getShiftColor(sk);
                            const shiftBg     = getShiftBg(sk);
                            const statusColor = STATUS_COLORS[j.status] || T.textMuted;
                            const statusIcon  = getStatusIcon(j.status);
                            const isJobSelected = selectedJobIds.has(j.schedule_id);
                            return (
                              <div
                                key={j.schedule_id}
                                onClick={() => deleteMode ? toggleJobSelection(j.schedule_id) : handleViewJobDetails(j, _client)}
                                style={{
                                  borderRadius: 11,
                                  border: `1px solid ${isJobSelected ? 'rgba(248,113,113,0.7)' : `${shiftColor}22`}`,
                                  padding: '12px 13px', cursor: 'pointer', marginBottom: 5,
                                  background: isJobSelected ? 'rgba(248,113,113,0.08)' : `linear-gradient(135deg, ${shiftColor}0A, transparent)`,
                                  boxShadow: isJobSelected ? '0 0 0 1px rgba(248,113,113,0.3)' : 'none',
                                  transition: 'all 0.14s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = isJobSelected ? 'rgba(248,113,113,0.14)' : `var(--nd-w05)`; e.currentTarget.style.transform = 'translateX(3px)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = isJobSelected ? 'rgba(248,113,113,0.08)' : `linear-gradient(135deg, ${shiftColor}0A, transparent)`; e.currentTarget.style.transform = 'translateX(0)'; }}
                              >
                                {/* Row 1 */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                    {deleteMode && (
                                      <div style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, border: `1.5px solid ${isJobSelected ? '#F87171' : 'var(--nd-tx25)'}`, background: isJobSelected ? '#F87171' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900, color: 'var(--nd-bg)' }}>
                                        {isJobSelected ? '✓' : ''}
                                      </div>
                                    )}
                                    <span style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", fontWeight: 700, background: shiftColor, color: 'var(--nd-bg)', padding: '3px 7px', borderRadius: 5, boxShadow: `0 1px 6px ${shiftColor}50` }}>
                                      {viewMode === 'maintenance' ? `${j.total_tasks || 0}T` : `#${j.service_number ?? '?'}`}
                                    </span>
                                    {sk && <span style={{ fontSize: 9, fontWeight: 700, color: shiftColor }}>{SHIFTS[sk]?.label || sk}</span>}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {statusIcon && <span style={{ fontSize: 9, color: statusColor }}>{statusIcon}</span>}
                                    <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.05em', color: statusColor, background: statusColor + '15', padding: '2px 7px', borderRadius: 5, border: `1px solid ${statusColor}25` }}>
                                      {(j.status || 'scheduled').replace('_', ' ').toUpperCase()}
                                    </span>
                                    {!deleteMode && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(j.schedule_id); }}
                                        title="Delete job"
                                        style={{ marginLeft: 2, width: 22, height: 22, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', color: '#F87171', fontSize: 11, lineHeight: 1, padding: 0 }}
                                      >🗑</button>
                                    )}
                                  </div>
                                </div>
                                {/* Row 2: team */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <div style={{ width: 18, height: 18, borderRadius: 6, background: 'var(--nd-w06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: T.textMuted, flexShrink: 0 }}>
                                    {(j.team_lead_name?.[0] || 'T').toUpperCase()}
                                  </div>
                                  <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }}>{j.team_lead_name || 'Unassigned'}</span>
                                  {j.site_name && (
                                    <span style={{ marginLeft: 'auto', fontSize: 9, color: 'var(--nd-w20)', maxWidth: 85, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.site_name}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          {/* Assign button for THIS specific client */}
                          <button
                            onClick={() => handleCellClick(_client, selectedDay)}
                            style={{ width: '100%', padding: '7px 14px', borderRadius: 8, border: '1px dashed rgba(59,130,246,0.35)', background: 'rgba(59,130,246,0.06)', color: '#60A5FA', fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', letterSpacing: '0.06em', marginTop: 3 }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.12)'; e.currentTarget.style.borderStyle = 'solid'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.06)'; e.currentTarget.style.borderStyle = 'dashed'; }}
                          >
                            + ASSIGN TO {client_name?.split(' ')[0]?.toUpperCase()}
                          </button>
                        </div>
                      ))
                    )}

                    {/* ✅ UPDATED: If clients exist, show the assignment list for clients NOT already scheduled today */}
                    {clients.length > 0 && clients.filter(c => !jobsByClient[c.client_id]).length > 0 && (
                      <div style={{ marginTop: 20 }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted, letterSpacing: '0.15em', marginBottom: 10 }}>
                          {selectedDayJobs.length === 0 ? 'ASSIGN TO CLIENT' : 'ASSIGN ANOTHER CLIENT'}
                        </div>
                        <input
                          className="novaa-input"
                          placeholder="Search clients…"
                          value={clientSearch}
                          onChange={e => setClientSearch(e.target.value)}
                          style={{ marginBottom: 10, padding: '8px 12px', fontSize: 12 }}
                        />
                        {(() => {
                          const q = clientSearch.trim().toLowerCase();
                          const list = clients
                            .filter(c => !jobsByClient[c.client_id])
                            .filter(c => !q || (c.client_name || '').toLowerCase().includes(q));
                          if (list.length === 0) {
                            return <div style={{ fontSize: 11, color: T.textMuted, padding: '8px 2px' }}>No matching clients.</div>;
                          }
                          return list.map(c => (
                            <button
                              key={c.client_id}
                              onClick={() => handleCellClick(c, selectedDay)}
                              style={{ width: '100%', padding: '8px 14px', borderRadius: 8, border: '1px dashed rgba(59,130,246,0.35)', background: 'rgba(59,130,246,0.06)', color: '#60A5FA', fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer', marginBottom: 5, transition: 'all 0.15s', textAlign: 'left' }}
                            >
                              + {c.client_name}
                            </button>
                          ));
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Month mini-stats footer */}
                  <div style={{ borderTop: `1px solid ${T.border}`, padding: '14px 16px', flexShrink: 0 }}>
                    <div style={{ fontSize: 8, fontWeight: 800, color: 'var(--nd-w15)', letterSpacing: '0.2em', marginBottom: 10 }}>MONTH OVERVIEW</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {[['TOTAL', stats.total, 'var(--nd-text)'], ['DONE', stats.completed, '#22D3A5'], ['ACTIVE', stats.active, '#60A5FA'], ['SCHED', stats.scheduled, '#94A3B8']].map(([label, val, color]) => (
                        <div key={label} style={{ padding: '10px 12px', background: 'var(--nd-w02)', borderRadius: 9, border: `1px solid ${T.border}` }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{loading ? '—' : val}</div>
                          <div style={{ fontSize: 7, fontWeight: 700, color: 'var(--nd-w20)', letterSpacing: '0.18em', marginTop: 3 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Nothing selected */
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📅</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nd-w20)', letterSpacing: '0.05em', marginBottom: 6 }}>Select a date</div>
                    <div style={{ fontSize: 10, color: 'var(--nd-w12)', lineHeight: 1.6 }}>Click any day in the<br />calendar to view & manage jobs</div>
                  </div>
                  <div style={{ width: '100%', marginTop: 8 }}>
                    <div style={{ height: 1, background: T.border, marginBottom: 14 }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {[['TOTAL', stats.total, 'var(--nd-text)'], ['DONE', stats.completed, '#22D3A5'], ['ACTIVE', stats.active, '#60A5FA'], ['SCHED', stats.scheduled, '#94A3B8']].map(([label, val, color]) => (
                        <div key={label} style={{ padding: '10px 12px', background: 'var(--nd-w02)', borderRadius: 9, border: `1px solid ${T.border}` }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{loading ? '—' : val}</div>
                          <div style={{ fontSize: 7, fontWeight: 700, color: 'var(--nd-w20)', letterSpacing: '0.18em', marginTop: 3 }}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════
          BULK ACTION BAR (floating)
      ══════════════════════════════════════════════════════════ */}
      {bulkMode && selectedDays.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9000,
          display: 'flex', alignItems: 'center', gap: 14,
          background: 'var(--nd-surface)', border: '1px solid rgba(34,211,165,0.4)', borderRadius: 14,
          padding: '12px 16px 12px 20px', boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
          animation: 'fadeIn 0.2s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#22D3A5', fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{selectedDays.size}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em' }}>DAY{selectedDays.size !== 1 ? 'S' : ''} SELECTED</span>
          </div>
          <div style={{ width: 1, height: 24, background: T.border }} />
          <button className="novaa-btn-ghost" onClick={() => setSelectedDays(new Set())}>Clear</button>
          <button className="novaa-btn-danger" onClick={openBulkDeleteModal}>🗑 Delete Jobs</button>
          <button className="novaa-btn-primary" onClick={openBulkModal}>Assign Jobs →</button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          MODAL 1: ASSIGN / EDIT MODAL
      ══════════════════════════════════════════════════════════ */}
      {showAssignModal && selectedCell && (
        <div className="novaa-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="novaa-modal fade-in" onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div style={{ padding: '22px 26px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.15em', marginBottom: 5 }}>
                  {viewMode === 'cleaning' ? '🧹 CLEANING' : '🔧 MAINTENANCE'} · {selectedCell.date}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em' }}>{selectedCell.clientName}</div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: T.textMuted, background: 'var(--nd-w05)', padding: '4px 10px', borderRadius: 6 }}>
                    Service #{selectedCell.serviceNumber}
                  </span>
                  {selectedCell.totalSites > 0 && (
                    <span style={{ fontSize: 11, color: '#60A5FA', background: 'rgba(96,165,250,0.1)', padding: '4px 10px', borderRadius: 6 }}>
                      📍 {selectedCell.totalSites} sites
                    </span>
                  )}
                  {selectedCell.monthlyServices && (
                    <span style={{ fontSize: 11, color: '#22D3A5', background: 'rgba(34,211,165,0.1)', padding: '4px 10px', borderRadius: 6 }}>
                      Contract: {selectedCell.monthlyServices}/mo
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setShowAssignModal(false)} style={{ background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, width: 34, height: 34, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            <div style={{ padding: '22px 26px' }}>

              {/* ── STEP: SITES ── */}
              {viewMode === 'cleaning' && assignmentStep === 'sites' && (
                <div>
                  <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 14 }}>STEP 1 · SELECT SITES TO SCHEDULE</div>
                  {loadingSites ? (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading sites…</div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                        <button onClick={handleSelectAllSites} className="novaa-btn-ghost" style={{ fontSize: 12 }}>Select All Available</button>
                        <button onClick={handleDeselectAllSites} className="novaa-btn-ghost" style={{ fontSize: 12 }}>Deselect All</button>
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textMuted, alignSelf: 'center' }}>{selectedSiteIds.length} selected</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, maxHeight: 280, overflowY: 'auto', paddingRight: 4 }}>
                        {/* ✅ UPDATED: Disable previously assigned sites logic added here */}
                        {clientSites.map(site => {
                          const isAssigned = (selectedCell?.existingSchedules || []).map(s => Number(s.site_id)).includes(Number(site.id));
                          const isSelected = selectedSiteIds.includes(site.id);

                          return (
                            <div
                              key={site.id}
                              className={`novaa-site-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => { if (!isAssigned) toggleSiteSelection(site.id); }}
                              style={{ 
                                opacity: isAssigned ? 0.4 : 1, 
                                cursor: isAssigned ? 'not-allowed' : 'pointer',
                                background: isAssigned ? 'var(--nd-w02)' : undefined
                              }}
                            >
                              <div style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${isSelected ? '#60A5FA' : 'var(--nd-w20)'}`, background: isSelected ? '#60A5FA' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: 'var(--nd-text-strong)' }}>
                                {isSelected ? '✓' : ''}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                                {site.site_name || site.address}
                                {isAssigned && <span style={{ color: '#F87171', fontSize: 10, marginLeft: 6 }}><br/>(Already Assigned)</span>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                        <button disabled={selectedSiteIds.length === 0} className="novaa-btn-primary" onClick={() => setAssignmentStep('shift')}>
                          Next: Select Shift →
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── STEP: SHIFT ── */}
              {viewMode === 'cleaning' && assignmentStep === 'shift' && (
                <div>
                  <button className="novaa-back-link" onClick={() => setAssignmentStep('sites')}>← Back to Sites</button>
                  <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 16 }}>STEP 2 · SELECT SHIFT</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                    {['morning','afternoon','evening','night','overnight'].map(shift => {
                      const s = SHIFTS[shift];
                      const active = selectedShift === shift;
                      return (
                        <button
                          key={shift}
                          className={`novaa-step-btn ${active ? 'active' : ''}`}
                          onClick={() => setSelectedShift(shift)}
                          style={{ borderColor: active ? s.color : undefined, background: active ? s.bg : undefined, color: active ? s.color : undefined }}
                        >
                          <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color, marginBottom: 8, boxShadow: `0 0 6px ${s.color}` }} />
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{s.label}</div>
                          <div style={{ fontSize: 10, opacity: 0.65, marginTop: 2 }}>{s.time}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                    <button disabled={!selectedShift} className="novaa-btn-primary" onClick={() => setAssignmentStep('teams')}>Next: Select Team →</button>
                  </div>
                </div>
              )}

              {/* ── STEP: TEAMS ── */}
              {((viewMode === 'cleaning' && assignmentStep === 'teams') || viewMode === 'maintenance') && (
                <>
                  {viewMode === 'cleaning' && <button className="novaa-back-link" onClick={() => setAssignmentStep('shift')}>← Back to Shift</button>}

                  {/* Existing schedules */}
                  {selectedCell.existingSchedules.length > 0 && (
                    <div style={{ marginBottom: 26 }}>
                      <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 12 }}>EXISTING SCHEDULES</div>
                      {selectedCell.existingSchedules.map(s => {
                        const shiftData = SHIFTS[String(s.shift || s.status).toLowerCase()] || SHIFTS.rescheduled;
                        return (
                          <div key={s.schedule_id} style={{ background: 'var(--nd-w03)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#60A5FA', fontFamily: "'DM Mono', monospace" }}>#{s.service_number}</span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.team_lead_name || 'Unassigned'}</span>
                              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 9, fontWeight: 700, background: shiftData.bg, color: shiftData.color, letterSpacing: '0.06em' }}>
                                {shiftData.label.toUpperCase()}
                              </span>
                              <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 9, fontWeight: 700, background: 'var(--nd-w06)', color: T.textMuted }}>
                                {s.status?.toUpperCase()}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <select defaultValue={s.team_id || ''} onChange={e => handleUpdateSchedule(s.schedule_id, { team_id: Number(e.target.value) })} className="novaa-select" style={{ flex: 1, minWidth: 120 }}>
                                <option value="">Reassign Team</option>
                                {teams.map(t => <option key={t.id} value={t.id}>{t.team_lead_name}</option>)}
                              </select>
                              <select defaultValue={s.shift || ''} onChange={e => handleUpdateSchedule(s.schedule_id, { shift: e.target.value, previous_shift: s.shift })} className="novaa-select" style={{ flex: 1, minWidth: 120, borderColor: getShiftColor(s.shift) + '60' }}>
                                <option value="">Change Shift</option>
                                {['morning','afternoon','evening','night','overnight','day_extension'].map(sh => <option key={sh} value={sh}>{SHIFTS[sh]?.label || sh}</option>)}
                              </select>
                              <select defaultValue={s.status} onChange={e => handleUpdateSchedule(s.schedule_id, { status: e.target.value })} className="novaa-select" style={{ flex: 1, minWidth: 120 }}>
                                <option value="scheduled">Scheduled</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="rescheduled">Rescheduled</option>
                              </select>
                              <button onClick={() => { setRescheduleData({ scheduleId: s.schedule_id, originalDate: selectedCell.date, newDate: '', reason: '' }); setShowRescheduleModal(true); }} className="novaa-btn-ghost" style={{ color: '#F59E0B', borderColor: 'rgba(245,158,11,0.3)' }}>↷ Reschedule</button>
                              <button onClick={() => handleDeleteSchedule(s.schedule_id)} className="novaa-btn-danger">Delete</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Maintenance task selection */}
                  {viewMode === 'maintenance' && maintenanceTemplates.length > 0 && (
                    <div style={{ marginBottom: 22 }}>
                      <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 12 }}>MAINTENANCE TASKS</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {maintenanceTemplates.map(t => (
                          <div
                            key={t.id}
                            className={`novaa-site-card ${selectedTasks.includes(t.id) ? 'selected' : ''}`}
                            onClick={() => toggleTaskSelection(t.id)}
                            style={{ gap: 10 }}
                          >
                            <div style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${selectedTasks.includes(t.id) ? '#60A5FA' : 'var(--nd-w20)'}`, background: selectedTasks.includes(t.id) ? '#60A5FA' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: 'var(--nd-text-strong)' }}>
                              {selectedTasks.includes(t.id) ? '✓' : ''}
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{t.task_name || t.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shift selector for maintenance mode */}
                  {viewMode === 'maintenance' && (
                    <div style={{ marginBottom: 22 }}>
                      <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 12 }}>SELECT SHIFT</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {['morning','afternoon','evening','night','overnight'].map(shift => {
                          const s = SHIFTS[shift];
                          const active = selectedShift === shift;
                          return (
                            <button key={shift} onClick={() => setSelectedShift(shift)}
                              style={{ padding: '8px 14px', borderRadius: 8, border: `1.5px solid ${active ? s.color : 'var(--nd-w10)'}`, background: active ? s.bg : 'var(--nd-w03)', color: active ? s.color : T.textMuted, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Assign team */}
                  <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 12 }}>
                    {viewMode === 'cleaning' ? 'STEP 3 · ' : ''}ASSIGN TEAM
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>PETTY CASH FOR THE DAY (PKR) — OPTIONAL</label>
                    <input type="number" min="0" value={pettyCash} onChange={e => setPettyCash(e.target.value)} placeholder="e.g. 2000" className="novaa-input" style={{ maxWidth: 240 }} />
                  </div>
                  {teams.length === 0 ? (
                    <div style={{ padding: '20px 0', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No teams available</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
                      {teams.map(team => (
                        <div key={team.id} className="novaa-team-card" onClick={() => handleAssignTeam(team.id)}>
                          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#3B82F6,#22D3A5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 14, fontWeight: 800, color: 'var(--nd-bg)' }}>
                            {team.team_lead_name?.[0]?.toUpperCase() || 'T'}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 2 }}>{team.team_lead_name || `Team ${team.id}`}</div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>Tap to assign</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          MODAL 2: RESCHEDULE
      ══════════════════════════════════════════════════════════ */}
      {showRescheduleModal && (
        <div className="novaa-modal-overlay" onClick={() => setShowRescheduleModal(false)}>
          <div className="novaa-modal fade-in" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '22px 26px 18px', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.15em', marginBottom: 4 }}>RESCHEDULE JOB</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--nd-text-strong)' }}>Move to New Date</div>
              {rescheduleData.originalDate && (
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>Original date: <span style={{ color: '#F59E0B' }}>{rescheduleData.originalDate}</span></div>
              )}
            </div>
            <div style={{ padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: '0.12em', display: 'block', marginBottom: 8 }}>NEW DATE</label>
                <input
                  type="date"
                  className="novaa-input"
                  value={rescheduleData.newDate}
                  onChange={e => setRescheduleData(prev => ({ ...prev, newDate: e.target.value }))}
                  style={{ colorScheme: 'dark' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: '0.12em', display: 'block', marginBottom: 8 }}>REASON FOR RESCHEDULING</label>
                <textarea
                  className="novaa-input"
                  value={rescheduleData.reason}
                  onChange={e => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Explain why this job is being moved…"
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button className="novaa-btn-ghost" onClick={() => setShowRescheduleModal(false)}>Cancel</button>
                <button className="novaa-btn-primary" onClick={handleRescheduleSubmit}>↷ Confirm Reschedule</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          MODAL 4: BULK MULTI-DAY ASSIGN
      ══════════════════════════════════════════════════════════ */}
      {showBulkModal && (
        <div className="novaa-modal-overlay" onClick={() => { if (!bulkSubmitting) setShowBulkModal(false); }}>
          <div className="novaa-modal fade-in" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ padding: '22px 26px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.15em', marginBottom: 5 }}>⊞ BULK ASSIGN · {monthNames[selectedMonth - 1]} {selectedYear}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em' }}>
                  {selectedDays.size} day{selectedDays.size !== 1 ? 's' : ''} selected
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', maxWidth: 620 }}>
                  {[...selectedDays].sort((a, b) => a - b).map(d => (
                    <span key={d} style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", fontWeight: 700, color: '#22D3A5', background: 'rgba(34,211,165,0.1)', border: '1px solid rgba(34,211,165,0.25)', borderRadius: 6, padding: '2px 7px' }}>
                      {monthNames[selectedMonth - 1].slice(0, 3)} {d}
                    </span>
                  ))}
                </div>
              </div>
              <button onClick={() => { if (!bulkSubmitting) setShowBulkModal(false); }} style={{ background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, width: 34, height: 34, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            <div style={{ padding: '22px 26px' }}>

              {/* ── STEP 1: CLIENT ── */}
              {bulkStep === 'client' && (
                <div>
                  <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 14 }}>STEP 1 · SELECT CLIENT</div>
                  {clients.length === 0 ? (
                    <div style={{ padding: '30px 0', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No clients available</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, maxHeight: 340, overflowY: 'auto', paddingRight: 4 }}>
                      {clients.map(c => (
                        <div key={c.client_id} className="novaa-site-card" onClick={() => handleBulkClientPick(c.client_id)} style={{ justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.client_name}</span>
                          {c.total_sites > 0 && <span style={{ fontSize: 10, color: '#60A5FA' }}>📍 {c.total_sites}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP 2: SITES ── */}
              {bulkStep === 'sites' && (
                <div>
                  <button className="novaa-back-link" onClick={() => setBulkStep('client')}>← Back to Clients</button>
                  <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 14 }}>STEP 2 · SELECT SITES</div>
                  {loadingSites ? (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading sites…</div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                        <button onClick={() => setBulkSiteIds(clientSites.map(s => s.id))} className="novaa-btn-ghost" style={{ fontSize: 12 }}>Select All</button>
                        <button onClick={() => setBulkSiteIds([])} className="novaa-btn-ghost" style={{ fontSize: 12 }}>Deselect All</button>
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textMuted, alignSelf: 'center' }}>{bulkSiteIds.length} selected</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, maxHeight: 280, overflowY: 'auto', paddingRight: 4 }}>
                        {clientSites.map(site => {
                          const isSelected = bulkSiteIds.includes(site.id);
                          return (
                            <div key={site.id} className={`novaa-site-card ${isSelected ? 'selected' : ''}`} onClick={() => toggleBulkSite(site.id)}>
                              <div style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${isSelected ? '#60A5FA' : 'var(--nd-w20)'}`, background: isSelected ? '#60A5FA' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: 'var(--nd-text-strong)' }}>
                                {isSelected ? '✓' : ''}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{site.site_name || site.address}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                        <button disabled={bulkSiteIds.length === 0} className="novaa-btn-primary" onClick={() => setBulkStep('shift')}>Next: Select Shift →</button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── STEP 3: SHIFT ── */}
              {bulkStep === 'shift' && (
                <div>
                  <button className="novaa-back-link" onClick={() => setBulkStep('sites')}>← Back to Sites</button>
                  <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 16 }}>STEP 3 · SELECT SHIFT</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                    {['morning', 'afternoon', 'evening', 'night', 'overnight'].map(shift => {
                      const s = SHIFTS[shift];
                      const active = bulkShift === shift;
                      return (
                        <button key={shift} className={`novaa-step-btn ${active ? 'active' : ''}`} onClick={() => setBulkShift(shift)} style={{ borderColor: active ? s.color : undefined, background: active ? s.bg : undefined, color: active ? s.color : undefined }}>
                          <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color, marginBottom: 8, boxShadow: `0 0 6px ${s.color}` }} />
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{s.label}</div>
                          <div style={{ fontSize: 10, opacity: 0.65, marginTop: 2 }}>{s.time}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                    <button disabled={!bulkShift} className="novaa-btn-primary" onClick={() => setBulkStep('teams')}>Next: Select Team →</button>
                  </div>
                </div>
              )}

              {/* ── STEP 4: TEAMS ── */}
              {bulkStep === 'teams' && (
                <div>
                  <button className="novaa-back-link" onClick={() => setBulkStep('shift')}>← Back to Shift</button>
                  <div style={{ fontSize: 11, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 12 }}>STEP 4 · ASSIGN TEAM</div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
                    Assigning <b style={{ color: '#22D3A5' }}>{clients.find(c => c.client_id === bulkClientId)?.client_name}</b> · {bulkSiteIds.length} site(s) · {getShiftDisplayName(bulkShift)} · across <b style={{ color: '#60A5FA' }}>{selectedDays.size}</b> day(s)
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>PETTY CASH PER DAY (PKR) — OPTIONAL</label>
                    <input type="number" min="0" value={bulkPettyCash} onChange={e => setBulkPettyCash(e.target.value)} placeholder="e.g. 2000" className="novaa-input" style={{ maxWidth: 240 }} />
                  </div>
                  {teams.length === 0 ? (
                    <div style={{ padding: '20px 0', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No teams available</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, opacity: bulkSubmitting ? 0.5 : 1, pointerEvents: bulkSubmitting ? 'none' : 'auto' }}>
                      {teams.map(team => (
                        <div key={team.id} className="novaa-team-card" onClick={() => handleBulkAssign(team.id)}>
                          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#3B82F6,#22D3A5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 14, fontWeight: 800, color: 'var(--nd-bg)' }}>
                            {team.team_lead_name?.[0]?.toUpperCase() || 'T'}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 2 }}>{team.team_lead_name || `Team ${team.id}`}</div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>{bulkSubmitting ? 'Assigning…' : 'Tap to assign'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          MODAL 5: BULK DELETE BY CLIENT (multi-day)
      ══════════════════════════════════════════════════════════ */}
      {showBulkDeleteModal && (() => {
        const q = bulkDeleteSearch.trim().toLowerCase();
        const rows = clients.map(c => {
          let jobCount = 0; const daySet = new Set();
          selectedDays.forEach(day => {
            const n = getSchedulesForDay(c, day).length;
            if (n > 0) { jobCount += n; daySet.add(day); }
          });
          return { c, jobCount, dayCount: daySet.size };
        }).filter(r => r.jobCount > 0)
          .filter(r => !q || (r.c.client_name || '').toLowerCase().includes(q));

        return (
          <div className="novaa-modal-overlay" onClick={() => { if (!bulkSubmitting) setShowBulkDeleteModal(false); }}>
            <div className="novaa-modal fade-in" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '22px 26px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.15em', marginBottom: 5 }}>🗑 DELETE JOBS · {selectedDays.size} day{selectedDays.size !== 1 ? 's' : ''}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em' }}>Pick a client to clear</div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>Deletes that client's jobs on the selected days.</div>
                </div>
                <button onClick={() => { if (!bulkSubmitting) setShowBulkDeleteModal(false); }} style={{ background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, width: 34, height: 34, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
              </div>
              <div style={{ padding: '18px 26px 24px' }}>
                <input
                  className="novaa-input"
                  placeholder="Search clients…"
                  value={bulkDeleteSearch}
                  onChange={e => setBulkDeleteSearch(e.target.value)}
                  style={{ marginBottom: 14 }}
                />
                {rows.length === 0 ? (
                  <div style={{ padding: '30px 0', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
                    {bulkDeleteSearch ? 'No matching clients with jobs on the selected days.' : 'No jobs on the selected days.'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto', paddingRight: 4, opacity: bulkSubmitting ? 0.5 : 1, pointerEvents: bulkSubmitting ? 'none' : 'auto' }}>
                    {rows.map(({ c, jobCount, dayCount }) => (
                      <div
                        key={c.client_id}
                        onClick={() => handleBulkDeleteByDates(c)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(248,113,113,0.25)', background: 'rgba(248,113,113,0.06)', cursor: 'pointer', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.14)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(248,113,113,0.06)'; }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{c.client_name}</span>
                        <span style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", fontWeight: 700, color: '#F87171' }}>{jobCount} job{jobCount !== 1 ? 's' : ''} · {dayCount} day{dayCount !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════════════════════
          MODAL 3: JOB DETAILS
      ══════════════════════════════════════════════════════════ */}
      <Modal show={detailsOpen} onHide={() => setDetailsOpen(false)} size="lg" centered className="novaa-cal">
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20, overflow: 'hidden', fontFamily: "'DM Sans', sans-serif", color: T.text }}>
          {detailsLoading && !selectedJob ? (
            <div style={{ padding: '60px 0', textAlign: 'center', color: T.textMuted }}>Loading job details…</div>
          ) : selectedJob && (
            <>
              {/* Details Header */}
              <div style={{ padding: '24px 28px', background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(34,211,165,0.1))', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.15em', marginBottom: 6 }}>
                      {selectedJob.job_type === 'maintenance' ? '🔧 MAINTENANCE' : '🧹 CLEANING'} JOB DETAILS
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--nd-text-strong)', marginBottom: 4 }}>{selectedJob.client_name}</div>
                    <div style={{ fontSize: 14, color: T.textMuted }}>{selectedJob.site_name}</div>
                  </div>
                  {selectedJob.status && (
                    <div style={{ padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', background: STATUS_COLORS[selectedJob.status] + '25', color: STATUS_COLORS[selectedJob.status] || T.textMuted, border: `1px solid ${STATUS_COLORS[selectedJob.status] || T.border}40` }}>
                      {selectedJob.status.replace('_',' ').toUpperCase()}
                    </div>
                  )}
                </div>
              </div>

              {/* Details Body */}
              <div style={{ padding: '24px 28px', maxHeight: '65vh', overflowY: 'auto', background: T.surface }}>

                {/* Info grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
                  {[
                    [<BuildIcon style={{ fontSize:18 }}/>, '#3B82F6', '#1D3D7A', 'Job Type', selectedJob.job_type === 'maintenance' ? 'Maintenance' : 'Commercial Cleaning'],
                    [<AssignmentIcon style={{ fontSize:18 }}/>, '#22D3A5', '#0D3D33', 'Service #', selectedJob.service_number || 'N/A'],
                    [<EngineeringIcon style={{ fontSize:18 }}/>, '#A78BFA', '#2D1F5E', 'Team Lead', selectedJob.team_lead_name || 'Unassigned'],
                    [<LocationIcon style={{ fontSize:18 }}/>, '#FB923C', '#4A2000', 'Location', selectedJob.site_address || selectedJob.site_name || 'N/A'],
                    [<CalendarIcon style={{ fontSize:18 }}/>, '#60A5FA', '#1A2F5A', 'Scheduled', selectedJob.scheduled_date ? format(new Date(selectedJob.scheduled_date), 'MMM dd, yyyy') : 'N/A'],
                    selectedJob.shift && [<TimeIcon style={{ fontSize:18 }}/>, getShiftColor(selectedJob.shift), '', 'Shift', getShiftDisplayName(selectedJob.shift)],
                  ].filter(Boolean).map(([icon, color, bg, label, val], idx) => (
                    <div key={idx} style={{ background: 'var(--nd-w03)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: color + '20', border: `1.5px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>{icon}</div>
                      <div>
                        <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.1em', marginBottom: 3, fontWeight: 700 }}>{label.toUpperCase()}</div>
                        <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{val}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Timeline */}
                <div style={{ background: 'var(--nd-w02)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ScheduleIcon style={{ fontSize: 16, color: '#60A5FA' }} /> JOB TIMELINE
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {selectedJob.scheduled_date && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="timeline-dot" style={{ background: 'rgba(59,130,246,0.15)', border: '1.5px solid rgba(59,130,246,0.4)' }}><CalendarIcon style={{ color: '#3B82F6', fontSize: 18 }} /></div>
                        <div><div style={{ fontSize: 11, fontWeight: 700, color: '#3B82F6', marginBottom: 1 }}>Scheduled</div><div style={{ fontSize: 12, color: T.textMuted }}>{format(new Date(selectedJob.scheduled_date), 'MMM dd, yyyy')}</div></div>
                      </div>
                    )}
                    {selectedJob.started_at && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="timeline-dot" style={{ background: 'rgba(34,211,165,0.12)', border: '1.5px solid rgba(34,211,165,0.35)' }}><ScheduleIcon style={{ color: '#22D3A5', fontSize: 18 }} /></div>
                        <div><div style={{ fontSize: 11, fontWeight: 700, color: '#22D3A5', marginBottom: 1 }}>Job Started</div><div style={{ fontSize: 12, color: T.textMuted }}>{format(new Date(selectedJob.started_at), 'MMM dd, yyyy · hh:mm a')}</div></div>
                      </div>
                    )}
                    {selectedJob.before_photos_at && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="timeline-dot" style={{ background: 'rgba(167,139,250,0.12)', border: '1.5px solid rgba(167,139,250,0.35)' }}><PhotoIcon style={{ color: '#A78BFA', fontSize: 18 }} /></div>
                        <div><div style={{ fontSize: 11, fontWeight: 700, color: '#A78BFA', marginBottom: 1 }}>Before Photos</div><div style={{ fontSize: 12, color: T.textMuted }}>{format(new Date(selectedJob.before_photos_at), 'MMM dd, yyyy · hh:mm a')}</div></div>
                      </div>
                    )}
                    {selectedJob.after_photos_at && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="timeline-dot" style={{ background: 'rgba(249,168,212,0.12)', border: '1.5px solid rgba(249,168,212,0.35)' }}><PhotoIcon style={{ color: '#F9A8D4', fontSize: 18 }} /></div>
                        <div><div style={{ fontSize: 11, fontWeight: 700, color: '#F9A8D4', marginBottom: 1 }}>After Photos</div><div style={{ fontSize: 12, color: T.textMuted }}>{format(new Date(selectedJob.after_photos_at), 'MMM dd, yyyy · hh:mm a')}</div></div>
                      </div>
                    )}
                    {selectedJob.completed_at && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="timeline-dot" style={{ background: 'rgba(34,211,165,0.15)', border: '1.5px solid rgba(34,211,165,0.5)' }}><CheckCircleIcon style={{ color: '#22D3A5', fontSize: 18 }} /></div>
                        <div><div style={{ fontSize: 11, fontWeight: 700, color: '#22D3A5', marginBottom: 1 }}>Completed</div><div style={{ fontSize: 12, color: T.textMuted }}>{format(new Date(selectedJob.completed_at), 'MMM dd, yyyy · hh:mm a')}</div></div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Photos */}
                {(selectedJob.before_photos || selectedJob.after_photos) && (
                  <div style={{ background: 'var(--nd-w02)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <PhotoIcon style={{ fontSize: 16, color: '#A78BFA' }} /> PHOTO GALLERY
                    </div>
                    {selectedJob.before_photos && (() => {
                      try { const photos = JSON.parse(selectedJob.before_photos); if (!photos.length) return null;
                        return <div style={{ marginBottom: 16 }}><div style={{ fontSize: 10, color: T.textMuted, marginBottom: 10, letterSpacing: '0.08em' }}>BEFORE</div><div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px,1fr))', gap: 8 }}>{photos.map((p, i) => <img key={i} className="photo-thumb" src={`https://app.sowashusa.com/uploads/job-photos/${p.split('/').pop()}`} alt={`Before ${i+1}`} onClick={() => window.open(`https://app.sowashusa.com/uploads/job-photos/${p.split('/').pop()}`, '_blank')} />)}</div></div>;
                      } catch { return null; }
                    })()}
                    {selectedJob.after_photos && (() => {
                      try { const photos = JSON.parse(selectedJob.after_photos); if (!photos.length) return null;
                        return <div><div style={{ fontSize: 10, color: T.textMuted, marginBottom: 10, letterSpacing: '0.08em' }}>AFTER</div><div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px,1fr))', gap: 8 }}>{photos.map((p, i) => <img key={i} className="photo-thumb" src={`https://app.sowashusa.com/uploads/job-photos/${p.split('/').pop()}`} alt={`After ${i+1}`} onClick={() => window.open(`https://app.sowashusa.com/uploads/job-photos/${p.split('/').pop()}`, '_blank')} />)}</div></div>;
                      } catch { return null; }
                    })()}
                  </div>
                )}

                {/* Conditional report */}
                {selectedJob.status === 'completed' && (
                  selectedJob.job_type === 'maintenance' ? (
                    <div style={{ background: 'rgba(192,38,211,0.06)', border: '1px solid rgba(192,38,211,0.25)', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#C026D3', letterSpacing: '0.12em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <EngineeringIcon style={{ fontSize: 16 }} /> MAINTENANCE REPORT
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>{selectedJob.task_name || 'General Maintenance'}</div>
                      {(selectedJob.task_description || selectedJob.maintenance_notes) && (
                        <div style={{ background: 'var(--nd-w04)', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: 13, color: T.textMuted, lineHeight: '1.6' }}>
                          {selectedJob.task_description || selectedJob.maintenance_notes}
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {[['Safety Check','PASSED','#22D3A5'], ['System Status','VERIFIED','#22D3A5']].map(([label,val,color]) => (
                          <div key={label} style={{ background: 'var(--nd-w03)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 600 }}>{label}</span>
                            <span style={{ fontSize: 10, fontWeight: 800, color, background: color+'20', padding: '3px 9px', borderRadius: 6, letterSpacing: '0.06em' }}>{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: 'var(--nd-w02)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <DescriptionIcon style={{ fontSize: 16, color: '#60A5FA' }} /> FIELD SERVICE REPORT
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {[['Cable Condition', selectedJob.cable_condition], ['Panel Damage', selectedJob.panel_damage], ['Inverter Alarm', selectedJob.inverter_alarm], ['Potential Shading', selectedJob.potential_shading]].map(([label, val]) => (
                          <div key={label} style={{ background: 'var(--nd-w03)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 14px' }}>
                            <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.1em', marginBottom: 4, fontWeight: 700 }}>{label.toUpperCase()}</div>
                            <div style={{ fontSize: 13, color: T.text, fontWeight: 700 }}>{val || 'N/A'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}

                {/* FSR Photo */}
                {selectedJob.fsr_photo && (
                  <div style={{ background: 'var(--nd-w02)', border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.12em', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DescriptionIcon style={{ fontSize: 16, color: '#22D3A5' }} /> FSR PHOTO
                    </div>
                    <img
                      className="photo-thumb"
                      style={{ maxWidth: 240, height: 170, objectFit: 'cover' }}
                      src={`https://app.sowashusa.com/uploads/job-photos/${String(selectedJob.fsr_photo).split('/').pop()}`}
                      alt="FSR"
                      onClick={() => window.open(`https://app.sowashusa.com/uploads/job-photos/${String(selectedJob.fsr_photo).split('/').pop()}`, '_blank')}
                    />
                  </div>
                )}

                {/* Notes */}
                {selectedJob.notes && (
                  <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: '16px 18px', marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', letterSpacing: '0.1em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <NotesIcon style={{ fontSize: 14 }} /> NOTES
                    </div>
                    <div style={{ fontSize: 13, color: T.textMuted, lineHeight: '1.65' }}>{selectedJob.notes}</div>
                  </div>
                )}

                {/* Signature */}
                {selectedJob.customer_signature && (
                  <div style={{ background: 'var(--nd-w02)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 18px' }}>
                    <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.1em', marginBottom: 10, fontWeight: 700 }}>CUSTOMER SIGNATURE</div>
                    <img
                      src={selectedJob.customer_signature.startsWith('http') ? selectedJob.customer_signature : `https://app.sowashusa.com/uploads/signatures/${selectedJob.customer_signature}`}
                      alt="Signature"
                      style={{ maxWidth: 280, height: 80, objectFit: 'contain', background: 'var(--nd-tx85)', borderRadius: 8, padding: '6px 12px' }}
                    />
                  </div>
                )}
              </div>

              {/* Details footer */}
              <div style={{ padding: '18px 28px', borderTop: `1px solid ${T.border}`, background: T.surface, display: 'flex', gap: 10 }}>
                <button onClick={() => setDetailsOpen(false)} className="novaa-btn-ghost" style={{ flex: 1 }}>Close</button>
                <button onClick={handleDownloadReport} className="novaa-btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <DownloadIcon style={{ fontSize: 18 }} /> Download Report
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default UnifiedSchedulingCalendar;